import { jwtDecode } from 'jwt-decode'
import Swal2 from 'sweetalert2'
import _orderBy from 'lodash.orderby'

/**
 * Description
 * @param {string} string
 */
export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function getCurrentDateEnUsFormat(today = new Date()) {
  // Define the options for formatting the date
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }

  // Use Intl.DateTimeFormat to format the date
  const formatter = new Intl.DateTimeFormat('en-US', options)
  const formattedDate = formatter.format(today)

  return formattedDate
}
/**
 * 
 * @param {Date} [date] 
 * @param {string} [format] 
 * @param {Intl.DateTimeFormatOptions} [options] 
 */
export function dateFormatWithTime(date = new Date(), format = 'sv-SE', options = null) {
  const formatter = new Intl.DateTimeFormat(format, options || {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const formattedDate = formatter.format(date)
  return formattedDate
}

/**
 * Description
 * @param {Date} date
 * @returns {string}
 */
export function getCurrentDate(date = new Date(), format = 'sv-SE') {
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }
  const formatter = new Intl.DateTimeFormat(format, options)
  const formattedDate = formatter.format(date)
  return formattedDate
}

/**
 * Description
 * @param {Date} [date]
 * @param {format} [string]
 * @returns {string|null}
 */
export function formatDate(date, format = 'sv-SE') {
  if (!date) return null
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }
  const formatter = new Intl.DateTimeFormat(format, options)
  const formattedDate = formatter.format(date)
  return formattedDate
}

/**
 * Description
 * @param {string} str
 * @returns {string}
 */
export function camelToSnake(str) {
  return str.replace(/([A-Z])/g, letter => `_${letter.toLowerCase()}`)
}

/**
 * Description
 * @param {number} number
 * @returns {string}
 */
export function formatAccounting(number) {
  // Create a NumberFormat object for the desired locale and options
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true, // ensures thousand separators are used
  })

  // Format the number
  const formattedNumber = formatter.format(Math.abs(number))

  // Handle negative numbers with parentheses
  return number < 0 ? `(${formattedNumber})` : formattedNumber
}

/**
 * Description
 * @param {number} number
 * @returns {string}
 */
export function formatNumber(number) {
  // Create a NumberFormat object for the desired locale and options
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    useGrouping: true, // ensures thousand separators are used
  })

  // Format the number
  const formattedNumber = formatter.format(Math.abs(number))

  // Handle negative numbers with parentheses
  return number < 0 ? `(${formattedNumber})` : formattedNumber
}

export const toPercent = (val = '0', decimal = 1) => Number(val).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: decimal })

export const isJwtExpired = (token) => {
  try {
    const decoded = jwtDecode(token)
    const exp = decoded.exp
    if (!exp) {
      return { expired: true, message: 'No expiration time found in the token.' }
    }

    const currentTime = Date.now() / 1000

    if (currentTime >= exp) {
      return { expired: true, message: 'Token is expired.' }
    } else {
      const expirationDate = new Date(exp * 1000)
      return { expired: false, message: `Token is valid until ${expirationDate}.` }
    }
  } catch (error) {
    return { expired: true, message: 'Invalid token.' }
  }
}

export const useWatchEffect = () => {
  const cleanupCache = new Map()

  const onCleanup = ({ key }, callback) => {
    const cleanup = cleanupCache.get(key)
    cleanup?.()
    cleanupCache.set(key, callback)
  }

  /**
   * Description
   * @param {(onCleanup: () => void) => void} callback
   * @param {string} key
   */
  const action = (callback, key) => {
    callback(onCleanup.bind({ key }))
    return {
      remove: () => cleanupCache.delete(key),
    }
  }

  const clear = () => {
    cleanupCache.forEach(cleanup => {
      cleanup?.()
    })
    cleanupCache.clear()
  }

  return /** @type {const} */ ([
    action,
    clear,
  ])
}

/**
 * Description
 * @param {number} [delay] default = 500 ms
 */
export const useDebounce = (delay = 500) => {
  let timeout = null
  return (callback) => {
    clearTimeout(timeout)
    timeout = setTimeout(callback, delay)
  }
}

/**
 * Downloads data as a CSV file.
 * 
 * @param {Array<Array<string|number>>} data - The data to be converted to CSV. Each sub-array represents a row.
 * @param {string} filename - The name of the file to be downloaded.
 */
export function downloadCSV(data, filename) {
  const dataString = data.map(row =>
    row.map(cell => `${String(cell)}`).join(','),
  ).join('\r\n')
  const blob = new Blob([dataString], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export function firstDayOfMonth() {
  const date = new Date()
  date.setDate(1)
  return getCurrentDate(date)
}

export function hasKeysOf(sourceObject, targetObject) {
  const targetKeys = Object.keys(targetObject)
  return targetKeys.every(key => key in sourceObject)
}

export const LoadingPopup = Swal2.mixin({
  title: 'Loading...',
  icon: 'info',
  allowOutsideClick: false,
  didOpen() {
    Swal2.showLoading()
  },
})

export const ErrorPopup = Swal2.mixin({
  title: 'Error',
  icon: 'error',
  text: 'Oops something went wrong!!!',
  allowEscapeKey: true,
  allowEnterKey: true,
  showConfirmButton: true,
  didOpen() {
    Swal2.hideLoading()
  },
})

export const SuccessPopup = Swal2.mixin({
  title: 'Success',
  icon: 'success',
  allowEscapeKey: true,
  allowEnterKey: true,
  showConfirmButton: true,
  didOpen() {
    Swal2.hideLoading()
  },
})

export const ConfirmPopup = Swal2.mixin({
  title: 'Confirm Void Receipt',
  icon: 'info',
  html: `
    <p>Are you sure you want to void this receipt?</p>
  `,
  showCloseButton: true,
  showCancelButton: true,
  focusConfirm: false,
  confirmButtonText: 'Confirm',
  cancelButtonText: 'Cancel',
})

/**
 * Description
 * @param {string} unit
 * @param {number} quantity
 * @returns {number}
 */
export const dynamicUnit = function (unit, quantity) {
  if (!unit) return quantity
  const [factor, baseUnit] = unit?.split('-')
  if (!baseUnit) return quantity
  const [numerator, denominator] = factor.split('/')
  if (!denominator) return factor * quantity
  return numerator / denominator
}


/**
 * Description
 * @param {number} number
 * @param {number} decimalPoints
 * @returns {number}
 */
export const roundOff = (number, decimalPoints = 1) => {
  const multiplier = Math.pow(10, decimalPoints)
  const result = (Math.round((number * multiplier)) / multiplier).toFixed(decimalPoints)
  return result * 1
}

export const orderBy = _orderBy


/**
 * Description
 * @param {lastTime: number, buffer: string} lastTime=
 * @returns {(key: string, callback: (buffer: string) => void)}
 */
export const initBarcodeEventHandler = (lastTime = Date.now(), buffer = '') => (key, callback) => {
  const now = Date.now()
  if (now - lastTime > 100) buffer = ''
  lastTime = now

  const isAllowed =
    key === 'Enter' ||
    /^[0-9a-zA-Z-]$/.test(key)

  if (!isAllowed) return

  const char = key === 'Enter' ? '' : key

  if (key === 'Enter') { // Enter key
    callback(buffer)
    buffer = ''
    return
  }
  buffer += char
}

export const Swal = Swal2